/** Homes, rooms, furniture, outdoors, pets and vehicles. */
import { c, dot, rr, type Glyph } from './geom.js';

const HOUSE = 'M3.5 11 12 4l8.5 7M5.5 9.5V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5';
const GARAGE = 'M3 20.5V9.5L12 4l9 5.5v11M7 20.5V13h10v7.5';

export const PLACES: Record<string, Glyph> = {
  home: `${HOUSE}M10 20v-5h4v5`,
  homes: 'M2 11.5 8 6.5l6 5M4 10v8.5h4M10 12.5 16 7.5l6 5M12 11.5V20h8v-8.5',
  building: 'M5 21V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v16M16 9h3a1 1 0 0 1 1 1v11M3 21h18M8.5 7h4M8.5 11h4M8.5 15h4',
  occupancy: `${HOUSE}${c(12, 12, 2)}M8.5 20a3.5 3.5 0 0 1 7 0`,
  'occupancy-on': {
    d: `${HOUSE}${c(12, 12, 2)}M8.5 20a3.5 3.5 0 0 1 7 0`,
    g: 'M6 9.6 12 4.6l6 5V19.5H6z',
  },
  bed: 'M3 18v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7M3 14h18M3 18v2M21 18v2M7 9V7.5A1.5 1.5 0 0 1 8.5 6h7A1.5 1.5 0 0 1 17 7.5V9',
  bath: 'M3 12h18v2.5a4.5 4.5 0 0 1-4.5 4.5h-9A4.5 4.5 0 0 1 3 14.5zM6 12V6.5a2.5 2.5 0 0 1 4.6-1.4M7 19l-1 2M17 19l1 2',
  shower: `M5 21V6.5A3.5 3.5 0 0 1 8.5 3h1A3.5 3.5 0 0 1 13 6.5V8M10 11a3 3 0 0 1 6 0z${dot(11, 14.5)}${dot(13, 15.5)}${dot(15, 14.5)}${dot(12, 18.5)}${dot(14, 18.5)}`,
  toilet: 'M6 4h5v6h5a4 4 0 0 1-4 4H9a3 3 0 0 1-3-3zM9 14l-1 7h7l-1-4',
  sofa: 'M5 11V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v3M3 13a2 2 0 0 1 4 0v2h10v-2a2 2 0 1 1 4 0v5H3zM5 18v2M19 18v2',
  kitchen: 'M5 10h14v6a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4zM3 11.5h2M19 11.5h2M9 3.5c-.7.9-.7 1.9 0 3M12 3.5c-.7.9-.7 1.9 0 3M15 3.5c-.7.9-.7 1.9 0 3',
  dining: `${c(12.5, 12, 4.5)}M3.5 3v5a1.5 1.5 0 0 0 3 0V3M5 9.5V21M20.5 21V3c-1.5 1-2 3-2 6s.5 3 2 3`,
  desk: `${rr(7.5, 3.5, 9, 6.5, 1.5)}M12 10v3M3 13h18M5 13v8M19 13v8`,
  stairs: 'M3 20h4.5v-4H12v-4h4.5V8H21',
  door: `M6 21V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21M4 21h16${dot(14.5, 12.5)}`,
  'door-open': {
    d: `M6 21V4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21M4 21h16M6 3.4 13 5.2V21${dot(11, 13)}`,
    g: 'M13 5.2 17.2 3.8V21H13z',
  },
  gate: 'M3 5v16M21 5v16M3 8.5h18M3 17h18M7.5 8.5V17M12 8.5V17M16.5 8.5V17',
  garage: `${GARAGE}M7 15.5h10M7 18h10`,
  'garage-open': { d: `${GARAGE}M7 15h10`, g: 'M7.6 15.6h8.8v4.9H7.6z' },
  dumbbell: 'M6.5 7v10M3.5 9.5v5M17.5 7v10M20.5 9.5v5M6.5 12h11',
  bike: `${c(6, 16, 3.5)}${c(18, 16, 3.5)}M6 16l3.5-7h6.5M11.5 16 9.5 9M11.5 16l4.5-7 2 7M16 9l-1-2.5h2.5M8 6.5h3`,
  shirt: 'M9 3 4 6l1.5 4L8 9v12h8V9l2.5 1L20 6l-5-3-3 2z',
  washer: `${rr(4, 3, 16, 18, 2.5)}${c(12, 13, 4.5)}${dot(7.5, 6.5)}${dot(10.5, 6.5)}`,
  // Control strip along the top, plates standing in the rack.
  dishwasher: `${rr(4, 3, 16, 18, 2.5)}M4 8h16${dot(7.5, 5.5)}${dot(10.5, 5.5)}M7.5 17.5h9M9 17.5V13M12 17.5v-5.5M15 17.5V13`,
  bin: 'M4 6.5h16M9 6.5V4h6v2.5M6 6.5l1 13.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13.5M10 10.5v7M14 10.5v7',
  box: 'M3 7.5 12 3l9 4.5v9L12 21l-9-4.5zM3 7.5 12 12l9-4.5M12 12v9',
  pipes: `M3 6h9a4 4 0 0 1 4 4v11M3 10h7a2 2 0 0 1 2 2v9${c(19, 5.5, 2.5)}M19 5.5 20 4.5`,
  tree: 'M12 3 6 12h3l-3 5h12l-3-5h3zM12 17v4',
  garden: 'M12 21v-8M12 13c0-3.5-2.5-6-6.5-6 0 3.5 2.5 6 6.5 6zM12 11c0-3 2-5.5 6-5.5 0 3.5-2.5 5.5-6 5.5zM7 21h10',
  leaf: 'M5 19c0-8 5-14 15-14 0 9-6 14-13 14zM5 19c3-4 6-7 9-9',
  'hot-tub': 'M3 12h18v4a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4zM7 9c0-1.5 1.5-1.5 1.5-3S7 3 7 3M12 9c0-1.5 1.5-1.5 1.5-3S12 3 12 3M17 9c0-1.5 1.5-1.5 1.5-3S17 3 17 3',
  paw: `M12 13c-2.5 0-5 2.6-5 4.8C7 19.3 8.3 20 9.6 20c1 0 1.6-.5 2.4-.5s1.4.5 2.4.5c1.3 0 2.6-.7 2.6-2.2 0-2.2-2.5-4.8-5-4.8z${c(4.8, 10, 1.7)}${c(8.8, 5.8, 1.8)}${c(15.2, 5.8, 1.8)}${c(19.2, 10, 1.7)}`,
  car: 'M4 16v-3.5l2-5A2 2 0 0 1 7.9 6h8.2a2 2 0 0 1 1.9 1.5l2 5V16a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1zM6.5 17v2.5M17.5 17v2.5M7 14h1.5M15.5 14H17M4.5 12h15',
  wrench: 'M14 3.5a5 5 0 0 0-4.3 7.3L3.5 17a2.1 2.1 0 0 0 3 3l6.2-6.2A5 5 0 0 0 20.5 10l-3 1-2.5-2.5 1-3z',
  person: `${c(12, 8, 4)}M4 21a8 8 0 0 1 16 0`,
  motion: `${c(14.5, 4.5, 1.75)}M13.5 7.5 12 13M13.2 8.8 10 10.5 9 13M13.2 8.8l2.3 2.7 2.5.3M12 13l-1.5 4L8 21M12 13l2.5 3.5 1 4.5`,
  'motion-on': {
    d: `${c(14.5, 4.5, 1.75)}M13.5 7.5 12 13M13.2 8.8 10 10.5 9 13M13.2 8.8l2.3 2.7 2.5.3M12 13l-1.5 4L8 21M12 13l2.5 3.5 1 4.5M2.5 9.5H6M2 13h3.5M3 16.5h3`,
    f: c(14.5, 4.5, 1.75),
  },
};
